import React, { useMemo } from "react";
import { v4 as uuid } from "uuid";

import { Point } from "../../types";
import { getActionPath } from "../../utils";
import styles from "./action-lines.module.scss";
import { ArrowMarker, HashpassMarker } from "./markers";
import { ActionLineProps } from "./types";

export interface HashpassProps extends ActionLineProps {
  numberOfHashes: number;
  classicMarkerHead?: boolean;
}

export const Hashpass = ({
  action,
  controlPoints,
  numberOfHashes,
  strokeWidth = 0.3,
  classicMarkerHead = false,
}: HashpassProps) => {
  const actionWithControlPoints = useMemo(
    () =>
      controlPoints
        ? {
            ...action,
            ...(controlPoints[0] && { pos: controlPoints[0] }),
            ...(controlPoints.slice(1, -1) && { midPoints: controlPoints.slice(1, -1) }),
            ...(controlPoints[controlPoints.length - 1] && { endPos: controlPoints[controlPoints.length - 1] }),
          }
        : action,
    [action, controlPoints]
  );

  const { pos, midPoints, endPos, color = "#000" } = actionWithControlPoints;
  const markerEndId = uuid();

  const isStraight = midPoints.length === 0;
  const straightMidPoints: Point[] = isStraight
    ? [{ x: (pos.x + endPos.x) / 2, y: (pos.y + endPos.y) / 2 }]
    : midPoints;

  const pathData = getActionPath({ pos, midPoints, endPos });

  const pathWithMidPoint =
    isStraight && straightMidPoints[0]
      ? `M ${pos.x} ${pos.y} L ${straightMidPoints[0].x} ${straightMidPoints[0].y} L ${endPos.x} ${endPos.y}`
      : midPoints[0]
      ? `M ${pos.x} ${pos.y} L ${midPoints[0].x} ${midPoints[0].y} L ${endPos.x} ${endPos.y}`
      : pathData;

  return (
    <>
      <ArrowMarker id={markerEndId} color={color} {...(classicMarkerHead && { xOffset: 2.5 })} />
      <HashpassMarker id={markerEndId} isStraight={isStraight} numberOfHashes={numberOfHashes} />
      <path
        d={pathData}
        className={styles.actionLine}
        markerEnd={`url(#arrow-${markerEndId})`}
        stroke={color}
        strokeWidth={strokeWidth}
      />
      <path
        d={pathWithMidPoint}
        className={styles.actionLine}
        markerMid={`url(#hashpass-${isStraight ? "STRAIGHT" : "PATH"}-${numberOfHashes}-${markerEndId})`}
        stroke="none"
        strokeWidth={strokeWidth}
      />
    </>
  );
};
